/**
 * Wastewater overlay for the live GHG report and GHG inventory page.
 *
 * Stored estimates exist in v_wastewater_inventory_summary (currently 2020).
 * Other years are scaled by service population (residents + workplace workers).
 * If workers are missing for the target year, resident population is used as a placeholder.
 * On the GHG inventory page, scaled wastewater is added to landfill waste as
 * Waste & wastewater. Missing wastewater is never assigned zero.
 */

import type { ReportSectorObservation } from './types';
import { reportValue } from './valueHelpers';

export const WASTEWATER_SOURCE_CONCLUSION = {
  statement: 1 as const,
  statementLabel:
    'Wastewater is calculated from the stored ClimatePlans wastewater inventory and included with landfill waste on the GHG inventory page',
  liveWasteIncludesWastewater: true,
  neverAssignMissingZero: true,
  sourceTable: 'v_wastewater_inventory_summary',
  sourceColumn: 'wastewater_emissions_mtco2e',
  methodologyVersionObserved: 'wastewater-1.0',
  efVersionObserved: 'EF-WW-1.0',
  observedCoverageNote:
    'v_wastewater_inventory_summary is populated for inventory_year 2020. Other years are scaled by service population (residents plus workplace workers), or by resident population when workers are unavailable for the inventory year. The GHG inventory page adds that wastewater estimate to landfill waste as Waste & wastewater.',
} as const;

export const WASTEWATER_BOUNDARY_COPY =
  'On the GHG inventory page, landfill waste and wastewater are combined as Waste & wastewater. Wastewater is also shown as its own section on the Waste + page. When the stored wastewater inventory year differs from the inventory year, the value is scaled by service population (residents plus workplace workers), or by resident population if workers are unavailable.';

export interface StoredWastewaterObservation {
  geofips: string;
  inventoryYear: number;
  mtCo2e: number | null;
  methodologyVersion: string | null;
  efVersion: string | null;
  coverageStatus: string | null;
}

export interface WastewaterScaleDenominators {
  inventoryPopulation: number | null;
  inventoryWorkers: number | null;
  basePopulation: number | null;
  baseWorkers: number | null;
}

export type WastewaterScaleBasis = 'year_matched' | 'service_population' | 'population_placeholder';

export interface ResolvedWastewater {
  mtCo2e: number;
  scaleBasis: WastewaterScaleBasis;
  scaleFactor: number;
  baseYear: number;
  baseMt: number;
  inventoryPopulation: number | null;
  inventoryWorkers: number | null;
  basePopulation: number | null;
  baseWorkers: number | null;
}

function positive(n: number | null | undefined): n is number {
  return n != null && Number.isFinite(n) && n > 0;
}

export function wastewaterGeofipsKeys(geofips: number): string[] {
  const raw = String(geofips);
  const padded = raw.padStart(7, '0');
  return raw === padded ? [raw] : [raw, padded];
}

export function resolveWastewaterForInventoryYear(
  stored: StoredWastewaterObservation,
  inventoryYear: number,
  denoms: WastewaterScaleDenominators | null
): ResolvedWastewater | null {
  if (stored.mtCo2e == null || !Number.isFinite(stored.mtCo2e)) return null;
  if (stored.inventoryYear === inventoryYear) {
    return {
      mtCo2e: stored.mtCo2e,
      scaleBasis: 'year_matched',
      scaleFactor: 1,
      baseYear: stored.inventoryYear,
      baseMt: stored.mtCo2e,
      inventoryPopulation: denoms?.inventoryPopulation ?? null,
      inventoryWorkers: denoms?.inventoryWorkers ?? null,
      basePopulation: denoms?.basePopulation ?? null,
      baseWorkers: denoms?.baseWorkers ?? null,
    };
  }
  if (!denoms) return null;

  const invPop = denoms.inventoryPopulation;
  const basePop = denoms.basePopulation;
  const invWorkers = denoms.inventoryWorkers;
  const baseWorkers = denoms.baseWorkers;

  if (positive(invPop) && positive(basePop) && positive(invWorkers) && positive(baseWorkers)) {
    const baseSp = basePop + baseWorkers;
    const invSp = invPop + invWorkers;
    if (!(baseSp > 0) || !Number.isFinite(invSp / baseSp)) return null;
    return {
      mtCo2e: stored.mtCo2e * (invSp / baseSp),
      scaleBasis: 'service_population',
      scaleFactor: invSp / baseSp,
      baseYear: stored.inventoryYear,
      baseMt: stored.mtCo2e,
      inventoryPopulation: invPop,
      inventoryWorkers: invWorkers,
      basePopulation: basePop,
      baseWorkers: baseWorkers,
    };
  }

  if (positive(invPop) && positive(basePop)) {
    const factor = invPop / basePop;
    if (!Number.isFinite(factor)) return null;
    return {
      mtCo2e: stored.mtCo2e * factor,
      scaleBasis: 'population_placeholder',
      scaleFactor: factor,
      baseYear: stored.inventoryYear,
      baseMt: stored.mtCo2e,
      inventoryPopulation: invPop,
      inventoryWorkers: invWorkers,
      basePopulation: basePop,
      baseWorkers: baseWorkers,
    };
  }

  return null;
}

export function wastewaterTableQualifier(sourceStatusText: string | null | undefined): string {
  if (sourceStatusText === 'scaled_service_population') {
    return 'scaled by service population; combined with landfill waste on the GHG inventory page';
  }
  if (sourceStatusText === 'scaled_population_placeholder') {
    return 'scaled by resident population as a placeholder; combined with landfill waste on the GHG inventory page';
  }
  if (sourceStatusText === 'year_matched_stored_inventory') {
    return 'stored wastewater inventory; combined with landfill waste on the GHG inventory page';
  }
  return 'combined with landfill waste on the GHG inventory page when available';
}

export function wastewaterScaleMemberNote(resolved: ResolvedWastewater): string {
  if (resolved.scaleBasis === 'year_matched') {
    return `Stored wastewater inventory for ${resolved.baseYear}. On the GHG inventory page this value is combined with landfill waste as Waste & wastewater.`;
  }
  if (resolved.scaleBasis === 'service_population') {
    return `Wastewater is scaled from the ${resolved.baseYear} ClimatePlans wastewater inventory (${resolved.baseMt} MT CO2e) by the change in service population (residents plus workplace workers). On the GHG inventory page this value is combined with landfill waste as Waste & wastewater.`;
  }
  return `Workplace workers were not available for the inventory year, so wastewater is scaled from the ${resolved.baseYear} ClimatePlans wastewater inventory (${resolved.baseMt} MT CO2e) by the change in resident population as a placeholder. On the GHG inventory page this value is combined with landfill waste as Waste & wastewater.`;
}

function overlayWastewaterSector(
  sector: ReportSectorObservation,
  resolved: ResolvedWastewater,
  inventoryYear: number
): ReportSectorObservation {
  const yearMatched = resolved.scaleBasis === 'year_matched';
  const calculationRule = yearMatched
    ? 'Copied from the existing ClimatePlans wastewater inventory. Combined with landfill waste on the GHG inventory page as Waste & wastewater.'
    : resolved.scaleBasis === 'service_population'
      ? `Stored wastewater_emissions_mtco2e (${resolved.baseYear}) × (inventory-year service population / ${resolved.baseYear} service population). Service population = residents + workplace workers. Combined with landfill waste on the GHG inventory page.`
      : `Stored wastewater_emissions_mtco2e (${resolved.baseYear}) × (inventory-year population / ${resolved.baseYear} population). Population placeholder because workplace workers were unavailable for the inventory year. Combined with landfill waste on the GHG inventory page.`;

  return {
    ...sector,
    emissionsMt: reportValue(resolved.mtCo2e, 'MT CO2e', yearMatched ? 'modeled' : 'extrapolated', {
      sourceTable: WASTEWATER_SOURCE_CONCLUSION.sourceTable,
      sourceColumn: WASTEWATER_SOURCE_CONCLUSION.sourceColumn,
      sourceYear: inventoryYear,
      calculationRule,
    }),
    notes: wastewaterScaleMemberNote(resolved),
    sourceStatusText: yearMatched
      ? 'year_matched_stored_inventory'
      : resolved.scaleBasis === 'service_population'
        ? 'scaled_service_population'
        : 'scaled_population_placeholder',
    sourceYear: resolved.baseYear,
  };
}

export function applyStoredOrScaledWastewater(
  sectors: ReportSectorObservation[],
  stored: StoredWastewaterObservation | null,
  inventoryYear: number,
  denoms: WastewaterScaleDenominators | null = null
): ReportSectorObservation[] {
  if (!stored) return sectors;
  const resolved = resolveWastewaterForInventoryYear(stored, inventoryYear, denoms);
  if (!resolved) return sectors;
  return sectors.map((s) =>
    s.sectorCode === 'water_wastewater' ? overlayWastewaterSector(s, resolved, inventoryYear) : s
  );
}

/** Overlay a year-matched stored wastewater row without adding it to Waste or the community total. */
export function applyYearMatchedWastewater(
  sectors: ReportSectorObservation[],
  stored: StoredWastewaterObservation | null,
  inventoryYear: number
): ReportSectorObservation[] {
  return applyStoredOrScaledWastewater(sectors, stored, inventoryYear, null);
}
